// client/src/components/ProductCard.js
import React, { useMemo, useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Select,
  MenuItem,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { CartContext } from "../context/CartContext.js";

const ouncesOrder = (label) => {
  const m = String(label).match(/(\d+(\.\d+)?)\s*oz/i);
  return m ? parseFloat(m[1]) : Number.POSITIVE_INFINITY;
};

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);

  // Normalize to [{label, unitAmount, currency, priceId}]
  const options = useMemo(() => {
    const raw = product?.sizes;

    // New stripe-only shape already: [{label, unitAmount, currency, priceId}]
    if (
      Array.isArray(raw) &&
      raw.length &&
      typeof raw[0] === "object" &&
      raw[0]?.label
    ) {
      const cleaned = raw.map((o) => ({
        label: String(o.label),
        unitAmount: Number(o.unitAmount ?? 0),
        currency: (o.currency || "CAD").toUpperCase(),
        priceId: o.priceId || null,
      }));
      cleaned.sort(
        (a, b) =>
          ouncesOrder(a.label) - ouncesOrder(b.label) ||
          a.label.localeCompare(b.label)
      );
      return cleaned;
    }

    // Legacy/reshaped: sizes: ["5oz","12oz"], plus *_BySize maps (and sometimes price:{})
    if (Array.isArray(raw)) {
      const cents = product?.priceCentsBySize || {};
      const ids = product?.priceIdBySize || {};
      const curr = product?.currencyBySize || {};
      const plainPrice = product?.price || {}; // strings like "7.99"

      const built = raw
        .filter((s) => typeof s === "string")
        .map((s) => {
          let unitAmount = Number(cents[s] ?? 0);
          if (!unitAmount && plainPrice[s]) {
            const n = Number(plainPrice[s]);
            if (!Number.isNaN(n)) unitAmount = Math.round(n * 100);
          }
          return {
            label: s,
            unitAmount,
            currency: (curr[s] || "CAD").toUpperCase(),
            priceId: ids[s] || null,
          };
        });

      built.sort(
        (a, b) =>
          ouncesOrder(a.label) - ouncesOrder(b.label) ||
          a.label.localeCompare(b.label)
      );
      return built;
    }

    // Last-ditch: price object only
    if (product?.price && typeof product.price === "object") {
      return Object.keys(product.price).map((label) => ({
        label,
        unitAmount: Math.round(Number(product.price[label]) * 100) || 0,
        currency: (product?.currencyBySize?.[label] || "CAD").toUpperCase(),
        priceId: product?.priceIdBySize?.[label] || null,
      }));
    }

    return [];
  }, [product]);

  const [selectedSize, setSelectedSize] = useState(
    options.length ? options[0].label : ""
  );

  useEffect(() => {
    if (!options.length) {
      setSelectedSize("");
      return;
    }
    if (!options.some((o) => o.label === selectedSize)) {
      setSelectedSize(options[0].label);
    }
  }, [options, selectedSize]);

  const selected = options.find((o) => o.label === selectedSize) || null;

  const priceText =
    selected &&
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: selected.currency || "CAD",
    }).format((selected.unitAmount || 0) / 100);

  const handleAddToCart = () => {
    if (options.length > 0 && !selected) return;
    addToCart(product, selected?.label || null);
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
      }}
    >
      {/* Image (unchanged) */}
      <Box
        sx={(theme) => ({
          position: "relative",
          p: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `
            radial-gradient(120px 120px at 30% 30%, ${alpha(
              theme.palette.secondary.main,
              0.18
            )} 0%, transparent 60%),
            radial-gradient(140px 140px at 75% 55%, ${alpha(
              theme.palette.primary.main,
              0.12
            )} 0%, transparent 60%)
          `,
        })}
      >
        <Box
          sx={{
            width: "100%",
            borderRadius: 2,
            backgroundColor: "background.paper",
            aspectRatio: "3 / 4",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={product.image}
            alt={product.name}
            loading="lazy"
            sx={{
              width: "72%",
              height: "72%",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
      </Box>

      <CardContent
        sx={{
          pt: 0.5,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={800}
          title={product.name}
          noWrap
          sx={{
            pr: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {product.name}
        </Typography>

        {!!options.length && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Size:
            </Typography>

            <Select
              size="small"
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
              displayEmpty
              sx={{ minWidth: 110 }}
            >
              {options.map((o) => (
                <MenuItem key={o.label} value={o.label}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>

            <Typography variant="body2" color="text.secondary">
              {selected ? `${priceText} (${selected.label})` : ""}
            </Typography>
          </Stack>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mt: 1 }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={handleAddToCart}
            disabled={options.length > 0 && !selected}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            Add to Cart
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={() =>
              navigate(`/products/${product.id}`, {
                state: selected ? { size: selected.label } : undefined,
              })
            }
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            View Details
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
